import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import WizardProgressIndicator from "components/ui/WizardProgressIndicator";

import Icon from "components/AppIcon";
import SummaryCard from "./components/SummaryCard";
import StandardProcessResults from "./components/StandardProcessResults";
import LooptFoundationResults from "./components/LooptFoundationResults";
import RagTraderRevenue from "./components/RagTraderRevenue";
import ValueChannelRevenue from "./components/ValueChannelRevenue";
import { useConfig } from "../../context/ConfigContext";

const ResultsDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("standard");
  const [calculationData, setCalculationData] = useState(null);
  const { config, loading: configLoading } = useConfig();

  useEffect(() => {
    // Only load results when config is available
    if (config) {
      loadResults();
    }
  }, [config]);

  const loadResults = () => {
    // Load actual data from localStorage and calculate results
      // Get data from localStorage
      const savedData = localStorage.getItem("materialInputData");

      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          const inputData = {
            material: parsedData.materialName || "Unknown Material",
            units: parseFloat(parsedData.units) || 0,
            materialWeight: parseFloat(parsedData.materialWeight) || 0,
            cogsPerUnit: parseFloat(parsedData.looptCogsPerUnit) || 0,
            // Loopt Foundation specific inputs
            looptUnits:
              parseFloat(parsedData.looptUnits) ||
              parseFloat(parsedData.units) ||
              0,
            looptMaterialWeight:
              parseFloat(parsedData.looptMaterialWeight) ||
              parseFloat(parsedData.materialWeight) ||
              0,
            looptCogsPerUnit: parseFloat(parsedData.looptCogsPerUnit) || 0,
            looptCorporateTaxRate:
              parseFloat(parsedData.looptCorporateTaxRate) || 0,
            // Revenue inputs
            ragTraderWeight:
              parseFloat(parsedData.ragTraderWeight) ||
              parseFloat(parsedData.materialWeight) ||
              0,
            valueChannelWeight:
              parseFloat(parsedData.valueChannelWeight) ||
              parseFloat(parsedData.materialWeight) ||
              0,
          };

          // Calculate results based on input data
          const calculatedData = calculateResults(inputData);
          setCalculationData(calculatedData);
        } catch (error) {
          console.error("Error parsing saved data:", error);
          setCalculationData(null);
        }
      } else {
        // No saved data available
        setCalculationData(null);
      }
    };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // Calculate all results based on input data
  const calculateResults = (inputData) => {
    if (!config) return null; // Guard clause

    // Standard Process calculations - using config values
    const standardFeePerUnit = config.standard_processing_fee;
    const standardTransportation = -config.standard_transportation;
    const standardProcessingFee = -(
      inputData.materialWeight * standardFeePerUnit
    );
    const standardProjectTotal = standardProcessingFee + standardTransportation;

    // Loopt Foundation calculations - using config values
    const looptFeePerPound = config.donation_processing_fee;
    const looptTransportation = -config.donation_transportation;
    const looptProcessingFee = -(
      inputData.looptMaterialWeight * looptFeePerPound
    );
    const looptCogsGross = inputData.looptUnits * inputData.looptCogsPerUnit;
    const looptFmv = looptCogsGross;
    const looptCorporateTaxRateDecimal = inputData.looptCorporateTaxRate / 100;
    const looptEbitaPositiveImpact = looptFmv * looptCorporateTaxRateDecimal;
    const looptNetEbitaContribution =
      looptProcessingFee + looptTransportation + looptEbitaPositiveImpact;

    // Revenue calculations - using config values
    const ragTraderRevenue = inputData.materialWeight * config.rag_trader_rate;
    const valueChannelRevenue = inputData.units * config.value_channel_rate;

    return {
      inputs: inputData,
      standardProcess: {
        material: inputData.material,
        units: inputData.units,
        materialWeight: inputData.materialWeight,
        feePerUnit: standardFeePerUnit,
        processingFee: standardProcessingFee,
        transportationEstimate: standardTransportation,
        projectTotal: standardProjectTotal,
      },
      looptFoundation: {
        material: inputData.material,
        units: inputData.looptUnits,
        materialWeight: inputData.looptMaterialWeight,
        cogsPerUnit: inputData.looptCogsPerUnit,
        feePerPound: looptFeePerPound,
        transportationEstimate: looptTransportation,
        corporateTaxRate: looptCorporateTaxRateDecimal,
        processingFee: looptProcessingFee,
        cogsGross: looptCogsGross,
        fmv: looptFmv,
        ebitaPositiveImpact: looptEbitaPositiveImpact,
        netEbitaContribution: looptNetEbitaContribution,
      },
      revenue: {
        ragTraderWeight: inputData.materialWeight,
        ragTraderRevenue: ragTraderRevenue,
        valueChannelWeight: inputData.materialWeight,
        valueChannelRevenue: valueChannelRevenue,
      },
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Show loading state while config loads
  if (configLoading) {
    return (
      <div className="min-h-screen">
        <WizardProgressIndicator />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading configuration...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <WizardProgressIndicator />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading-bold text-text-primary mb-2">
                Calculation Results
              </h1>
              <p className="text-text-secondary">
                Comprehensive analysis of your textile processing costs and
                revenue opportunities
              </p>
            </div>
            {/* Export Navigation */}
            <div className="flex justify-end">
              <button
                onClick={() => navigate("/export-summary")}
                className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg font-body-medium hover:bg-primary-600 transition-all duration-150 hover-scale focus-ring shadow-elevation-1"
              >
                <Icon name="Download" size={20} className="mr-2" />
                Export Results
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* Content based on data availability */}
        {calculationData ? (
          <>
            {/* Summary Card */}
            <div className="mb-8">
              <SummaryCard
                data={calculationData}
                formatCurrency={formatCurrency}
                formatNumber={formatNumber}
              />
            </div>

            {/* Main Results Content */}
            <div className="w-full">
              {/* Results Tabs */}
              <div className="w-full">
                <div className="bg-surface rounded-lg shadow-elevation-1">
                  {/* Desktop Tab Navigation */}
                  <div className="hidden md:block">
                    <div className="bg-gradient-to-r from-primary-50 via-accent-50 to-success-50 p-6 rounded-t-lg">
                      <div className="flex space-x-4">
                        <button
                          onClick={() => handleTabChange("standard")}
                          className={`flex-1 p-4 rounded-lg transition-all duration-300 ${
                            activeTab === "standard"
                              ? "bg-white shadow-lg transform scale-105"
                              : "bg-white/60 hover:bg-white/80 hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                activeTab === "standard"
                                  ? "bg-primary text-white"
                                  : "bg-primary-100 text-primary"
                              }`}
                            >
                              <Icon name="Settings" size={20} />
                            </div>
                            <div className="text-left">
                              <div
                                className={`font-body-medium ${
                                  activeTab === "standard"
                                    ? "text-primary"
                                    : "text-text-primary"
                                }`}
                              >
                                Standard Process
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => handleTabChange("loopt")}
                          className={`flex-1 p-4 rounded-lg transition-all duration-300 ${
                            activeTab === "loopt"
                              ? "bg-white shadow-lg transform scale-105"
                              : "bg-white/60 hover:bg-white/80 hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                activeTab === "loopt"
                                  ? "bg-success text-white"
                                  : "bg-success-100 text-success"
                              }`}
                            >
                              <Icon name="Recycle" size={20} />
                            </div>
                            <div className="text-left">
                              <div
                                className={`font-body-medium ${
                                  activeTab === "loopt"
                                    ? "text-success"
                                    : "text-text-primary"
                                }`}
                              >
                                Donation Model
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => handleTabChange("ragtrader")}
                          className={`flex-1 p-4 rounded-lg transition-all duration-300 ${
                            activeTab === "ragtrader"
                              ? "bg-white shadow-lg transform scale-105"
                              : "bg-white/60 hover:bg-white/80 hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                activeTab === "ragtrader"
                                  ? "bg-warning text-white"
                                  : "bg-warning-100 text-warning"
                              }`}
                            >
                              <Icon name="Recycle" size={20} />
                            </div>
                            <div className="text-left">
                              <div
                                className={`font-body-medium ${
                                  activeTab === "ragtrader"
                                    ? "text-warning"
                                    : "text-text-primary"
                                }`}
                              >
                                Rag Trader Revenue
                              </div>
                              <div className="text-xs text-text-tertiary">
                                Rag trader analysis
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => handleTabChange("valuechannel")}
                          className={`flex-1 p-4 rounded-lg transition-all duration-300 ${
                            activeTab === "valuechannel"
                              ? "bg-white shadow-lg transform scale-105"
                              : "bg-white/60 hover:bg-white/80 hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                activeTab === "valuechannel"
                                  ? "bg-primary text-white"
                                  : "bg-primary-100 text-primary"
                              }`}
                            >
                              <Icon name="TrendingUp" size={20} />
                            </div>
                            <div className="text-left">
                              <div
                                className={`font-body-medium ${
                                  activeTab === "valuechannel"
                                    ? "text-primary"
                                    : "text-text-primary"
                                }`}
                              >
                                Value Channel Revenue
                              </div>
                              <div className="text-xs text-text-tertiary">
                                Value channel analysis
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-6">
                    {activeTab === "standard" && (
                      <StandardProcessResults
                        data={calculationData}
                        formatCurrency={formatCurrency}
                        formatNumber={formatNumber}
                      />
                    )}

                    {activeTab === "loopt" && (
                      <LooptFoundationResults
                        data={calculationData}
                        formatCurrency={formatCurrency}
                        formatNumber={formatNumber}
                      />
                    )}

                    {activeTab === "ragtrader" && (
                      <RagTraderRevenue
                        data={calculationData}
                        formatCurrency={formatCurrency}
                        formatNumber={formatNumber}
                      />
                    )}

                    {activeTab === "valuechannel" && (
                      <ValueChannelRevenue
                        data={calculationData}
                        formatCurrency={formatCurrency}
                        formatNumber={formatNumber}
                      />
                    )}
                  </div>

                  {/* Mobile Tab Navigation */}
                  <div className="md:hidden">
                    <div className="bg-gradient-to-r from-primary-50 via-accent-50 to-success-50 p-4 rounded-t-lg">
                      <div className="space-y-2">
                        <button
                          onClick={() => handleTabChange("standard")}
                          className={`w-full p-3 rounded-lg transition-all duration-300 ${
                            activeTab === "standard"
                              ? "bg-white shadow-md"
                              : "bg-white/60 hover:bg-white/80"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                activeTab === "standard"
                                  ? "bg-primary text-white"
                                  : "bg-primary-100 text-primary"
                              }`}
                            >
                              <Icon name="Settings" size={16} />
                            </div>
                            <span
                              className={`text-sm font-body-medium ${
                                activeTab === "standard"
                                  ? "text-primary"
                                  : "text-text-primary"
                              }`}
                            >
                              Standard Process
                            </span>
                          </div>
                        </button>

                        <button
                          onClick={() => handleTabChange("loopt")}
                          className={`w-full p-3 rounded-lg transition-all duration-300 ${
                            activeTab === "loopt"
                              ? "bg-white shadow-md"
                              : "bg-white/60 hover:bg-white/80"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                activeTab === "loopt"
                                  ? "bg-success text-white"
                                  : "bg-success-100 text-success"
                              }`}
                            >
                              <Icon name="Recycle" size={16} />
                            </div>
                            <span
                              className={`text-sm font-body-medium ${
                                activeTab === "loopt"
                                  ? "text-success"
                                  : "text-text-primary"
                              }`}
                            >
                              Donation Model
                            </span>
                          </div>
                        </button>

                        <button
                          onClick={() => handleTabChange("ragtrader")}
                          className={`w-full p-3 rounded-lg transition-all duration-300 ${
                            activeTab === "ragtrader"
                              ? "bg-white shadow-md"
                              : "bg-white/60 hover:bg-white/80"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                activeTab === "ragtrader"
                                  ? "bg-warning text-white"
                                  : "bg-warning-100 text-warning"
                              }`}
                            >
                              <Icon name="Recycle" size={16} />
                            </div>
                            <span
                              className={`text-sm font-body-medium ${
                                activeTab === "ragtrader"
                                  ? "text-warning"
                                  : "text-text-primary"
                              }`}
                            >
                              Rag Trader Revenue
                            </span>
                          </div>
                        </button>

                        <button
                          onClick={() => handleTabChange("valuechannel")}
                          className={`w-full p-3 rounded-lg transition-all duration-300 ${
                            activeTab === "valuechannel"
                              ? "bg-white shadow-md"
                              : "bg-white/60 hover:bg-white/80"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                activeTab === "valuechannel"
                                  ? "bg-primary text-white"
                                  : "bg-primary-100 text-primary"
                              }`}
                            >
                              <Icon name="TrendingUp" size={16} />
                            </div>
                            <span
                              className={`text-sm font-body-medium ${
                                activeTab === "valuechannel"
                                  ? "text-primary"
                                  : "text-text-primary"
                              }`}
                            >
                              Value Channel Revenue
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* No Data State */
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-warning" />
            </div>
            <h3 className="text-xl font-heading-semibold text-text-primary mb-2">
              No Calculation Data Found
            </h3>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              It looks like you haven't completed a calculation yet. Please go
              back to the input forms to enter your material data.
            </p>
            <button
              onClick={() => navigate("/welcome-calculator-introduction")}
              className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg font-body-medium hover:bg-primary-600 transition-all duration-150 hover-scale focus-ring shadow-elevation-1"
            >
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Start New Calculation
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResultsDashboard;
